/** Earth radius in metres; matches the Rust workspace's `EARTH_RADIUS_M`. */
export const EARTH_RADIUS_M = 6_371_000;

const DEG_TO_RAD = Math.PI / 180;

/**
 * Great-circle distance in metres between two `[lat, lon]` points (degrees).
 * Callers must pass finite coords; non-finite inputs yield NaN, which any
 * `<=` comparison then answers `false` — guard upstream if that matters.
 */
export function haversineM(
  firstPoint: readonly [number, number],
  secondPoint: readonly [number, number],
): number {
  const [latFrom, lonFrom] = firstPoint;
  const [latTo, lonTo] = secondPoint;
  const deltaLat = (latTo - latFrom) * DEG_TO_RAD;
  const deltaLon = (lonTo - lonFrom) * DEG_TO_RAD;
  const sineHalfLat = Math.sin(deltaLat / 2);
  const sineHalfLon = Math.sin(deltaLon / 2);
  const haversine =
    sineHalfLat * sineHalfLat +
    Math.cos(latFrom * DEG_TO_RAD) * Math.cos(latTo * DEG_TO_RAD) * sineHalfLon * sineHalfLon;
  return 2 * EARTH_RADIUS_M * Math.asin(Math.min(1, Math.sqrt(haversine)));
}

/** True when the two points are within `radiusM` metres of each other. */
export function isCloseEnough(
  firstPoint: readonly [number, number],
  secondPoint: readonly [number, number],
  radiusM: number,
): boolean {
  return haversineM(firstPoint, secondPoint) <= radiusM;
}
